import React from "react"
import styled, { css } from "styled-components"

import { useLazyGetWarehouseListQuery } from "store/api/warehousesApi"
import { ApiTypes, UITypes } from "types"
import ProductType from "types/enums/ProductType"
import thermostatV2Image from "assets/img/thermostat_v2.png"
import thsImage from "assets/img/ths.png"
import wlsImage from "assets/img/wls.png"
import { ReactComponent as IconDevice } from "assets/svg/device.svg"
import { ReactComponent as IconDelete } from "assets/svg/icon-delete.svg"
import { ReactComponent as WireAdapterIcon } from "assets/svg/wire_adapter.svg"

import { getProductTypeLabel } from "utils/getProductTypeLabel"
import { useOrderTypeOptions } from "hooks/useOrderTypeOptions"

const PRODUCT_TYPE_SVG_ICONS: Partial<
  Record<ApiTypes.Model.OrderProductType, React.ComponentType<React.SVGProps<SVGSVGElement>>>
> = {
  thermostat: IconDevice,
  cwa: WireAdapterIcon,
}

const PRODUCT_TYPE_IMAGES: Partial<Record<ApiTypes.Model.OrderProductType, string>> = {
  thermostatV2: thermostatV2Image,
  ths: thsImage,
  wls: wlsImage,
}

import { OrderStatusBadge } from "components/common/OrderStatusBadge"
import { InputNumber } from "components/form/InputNumber"
import { SelectDynamic } from "components/form/SelectDynamic"
import { SelectStatic } from "components/form/SelectStatic"

import { OrderItem } from "./index"

const OrderItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: var(--surface-2);
  border-radius: 8px;
  margin-bottom: 12px;

  @media (max-width: 770px) {
    flex-wrap: wrap;
  }
`

const OrderItemIcon = styled.div<{ $imagePadding?: string }>`
  svg {
    width: 64px;
    height: 64px;
  }

  img {
    width: 64px;
    height: 64px;
    padding: ${({ $imagePadding }) => $imagePadding ?? "12px"};
    box-sizing: border-box;
    border-radius: 8px;
    border: 1px solid var(--border-default);
    background-color: var(--surface-1);
    object-fit: contain;
    display: block;
  }
`

const StatusActionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const OrderItemField = styled.div<{ isShort?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  ${({ isShort }) =>
    isShort &&
    css`
      flex: 0.8;
    `}

  @media (max-width: 770px) {
    min-width: 150px;
  }
`

const OrderItemValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`

const OrderItemQty = styled.span`
  font-size: 14px;
  color: var(--text-muted);
`

const OrderItemDelete = styled.div<{ $disabled?: boolean }>`
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--border-strong);
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;

  svg {
    stroke: #0f172a;
  }

  &:hover {
    background-color: var(--surface-2);

    svg {
      stroke: #ef4444;
    }
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.4;

      &:hover {
        background-color: transparent;

        svg {
          stroke: #0f172a;
        }
      }
    `}
`

interface OrderItemRowProps {
  item: OrderItem
  isEdit?: boolean
  index: number
  orderItems: OrderItem[]
  setOrderItems: React.Dispatch<React.SetStateAction<OrderItem[]>>
  isExisting?: boolean
}

export const OrderItemRow = ({
  item,
  isEdit,
  index,
  orderItems,
  setOrderItems,
  isExisting = false,
}: OrderItemRowProps) => {
  const orderTypeOptions = useOrderTypeOptions()
  const Icon = PRODUCT_TYPE_SVG_ICONS[item.productType]
  const image = PRODUCT_TYPE_IMAGES[item.productType]
  const imagePadding = item.productType === ProductType.ThermostatV2 ? "5px" : undefined
  const productLabel = getProductTypeLabel(item.productType) ?? ""

  const handleQuantityChange = (value: number) => {
    setOrderItems(orderItems.map((item, i) => (i === index ? { ...item, quantity: value } : item)))
  }

  const handleOrderTypeChange = (values: UITypes.Option[]) => {
    if (!values.length) return
    setOrderItems(orderItems.map((item, i) => (i === index ? { ...item, orderType: values } : item)))
  }

  const handleWarehouseChange = (values: UITypes.Option[]) => {
    setOrderItems(orderItems.map((item, i) => (i === index ? { ...item, warehouse: values } : item)))
  }

  const isRemovable = isEdit ? item.status === "new" : !isExisting
  const isLastSubOrder = orderItems.length <= 1
  const canDelete = isRemovable && (!isEdit || !isLastSubOrder)

  const handleDelete = () => {
    if (!canDelete) return
    setOrderItems(orderItems.filter((_, i) => i !== index))
  }

  const statusBadge = isExisting && item.status && (
    <div>
      <OrderStatusBadge status={item.status} />
    </div>
  )

  const deleteButton = (
    <OrderItemDelete
      onClick={handleDelete}
      $disabled={!canDelete}
      data-testid="order-item-delete"
      title={canDelete ? undefined : "At least one sub-order must remain in the order"}
    >
      <IconDelete />
    </OrderItemDelete>
  )

  return (
    <OrderItemWrapper>
      <OrderItemIcon $imagePadding={imagePadding}>
        {image ? <img src={image} alt={productLabel} /> : Icon && <Icon />}
      </OrderItemIcon>
      <OrderItemField>
        <OrderItemValue>{productLabel}</OrderItemValue>
        <OrderItemQty>Qty: {item.quantity}</OrderItemQty>
      </OrderItemField>
      <OrderItemField>
        <InputNumber minValue={1} onInputChange={handleQuantityChange} value={item.quantity} />
      </OrderItemField>
      <OrderItemField>
        <SelectStatic
          placeholder="Order type"
          values={item.orderType}
          options={orderTypeOptions}
          onChange={handleOrderTypeChange}
          isSearchable={false}
          backspaceDelete={false}
        />
      </OrderItemField>
      <OrderItemField>
        <SelectDynamic
          values={item.warehouse}
          valueName="id"
          labelName="name"
          dataTestId="vendor-order-warehouse-select"
          name={`warehouses-${item.id}`}
          useLazyQuery={useLazyGetWarehouseListQuery}
          placeholder="Warehouse"
          onChange={handleWarehouseChange}
          className=""
        />
      </OrderItemField>
      {isEdit ? (
        <OrderItemField>
          <StatusActionWrapper>
            {statusBadge}
            {isRemovable && deleteButton}
          </StatusActionWrapper>
        </OrderItemField>
      ) : (
        <>
          {statusBadge && <OrderItemField>{statusBadge}</OrderItemField>}
          {!isExisting && deleteButton}
        </>
      )}
    </OrderItemWrapper>
  )
}
